import { useCallback, useEffect, useState } from "preact/hooks";
import { reduce, type DemoAction } from "./reducer.js";
import { parseRoute, routeHref } from "./routes.js";
import { initialState, type DemoStore } from "./store.js";
import type { DemoRoute, DemoState } from "./types.js";

export interface DemoController {
  readonly state: DemoState;
  readonly persistent: boolean;
  dispatch(action: DemoAction): void;
  restart(): Promise<void>;
}

export type DemoSeed = (loaded: DemoState | null, now: number) => DemoState;

const keepLoaded: DemoSeed = (loaded, now) => loaded ?? initialState(now);

export function useDemo(
  open: () => Promise<DemoStore>,
  seed: DemoSeed = keepLoaded,
): DemoController | null {
  const [store, setStore] = useState<DemoStore | null>(null);
  const [state, setState] = useState<DemoState | null>(null);

  useEffect(() => {
    let live = true;
    void open().then(async (opened) => {
      const loaded = await opened.load().catch(() => null);
      if (live) {
        setStore(opened);
        setState(seed(loaded, Date.now()));
      }
    });
    return () => {
      live = false;
    };
  }, [open, seed]);

  const dispatch = useCallback(
    (action: DemoAction) => {
      setState((current) => {
        if (!current) {
          return current;
        }
        const next = reduce(current, action);
        if (next !== current) {
          void store?.save(next).catch(() => undefined);
        }
        return next;
      });
    },
    [store],
  );

  const restart = useCallback(async () => {
    await store?.clear().catch(() => undefined);
    setState(seed(null, Date.now()));
  }, [store, seed]);

  if (!store || !state) {
    return null;
  }
  return { state, persistent: store.persistent, dispatch, restart };
}

export function useHashRoute(): DemoRoute {
  const [route, setRoute] = useState(() => parseRoute(location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export function go(route: DemoRoute): void {
  location.hash = routeHref(route);
}

export function replaceRoute(route: DemoRoute): void {
  location.replace(routeHref(route));
}

export function useNow(interval = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(timer);
  }, [interval]);
  return now;
}
